import React from "react";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import { staticMeta } from "../lib/seo";

/**
 * /terms — plain-language terms of service.
 * Per owner spec (SEO pass §7): acceptable use, moderation/approval
 * model, upload license grant (INCLUDING search engines + shareable
 * URLs), uploader confirms ownership, entity = "CardCapture", plain
 * language.
 */
export default function TermsPage() {
  const meta = staticMeta("terms");
  return (
    <div
      className="mx-auto w-full max-w-[900px] px-5 py-10 md:px-gutter md:py-14"
      data-testid="terms-page"
    >
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:site_name" content="CardCapture" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content={meta.ogType} />
        <meta property="og:image" content={meta.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.ogImage} />
      </Helmet>

      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Terms" }]} />

      <div className="mt-6 text-eyebrow uppercase text-indigo-text">
        Terms
      </div>
      <h1 className="mt-1 text-title-page text-ink-text">Terms of Service</h1>

      <div className="mt-6 flex flex-col gap-6 text-body text-ink-muted">
        <section>
          <h2 className="text-title-section text-ink-text">Who we are</h2>
          <p className="mt-2">
            CardCapture is a community reference library operated as
            &ldquo;CardCapture&rdquo;. These terms apply to everyone who
            uses the site — signed-in contributors and anonymous visitors.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Acceptable use</h2>
          <p className="mt-2">
            Do not upload content that is not a real photo or short video
            of the trading card the page describes. Do not upload other
            people&rsquo;s photos without permission. Do not upload
            content that is unlawful, hateful, harassing, sexually
            explicit, or infringing on intellectual property. Do not
            attempt to disrupt, probe, or reverse engineer the service.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Moderation and approval</h2>
          <p className="mt-2">
            All submissions are reviewed by moderators before appearing
            publicly. Submissions that violate these terms are rejected.
            Approved submissions may be re-reviewed and removed at any
            time. Repeat violators may have their accounts suspended.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Upload license grant</h2>
          <p className="mt-2">
            By uploading a photo or video to CardCapture you confirm that
            you own it (or have the rights to publish it) and you grant
            CardCapture a worldwide, royalty-free, non-exclusive licence
            to host, reproduce, display, and distribute that content on
            the card page you attached it to, in social preview cards
            when the URL is shared, and in indexes maintained by
            legitimate search engines. This licence is granted for the
            purpose of running the reference library and terminates when
            you delete the content or your account (subject to a
            reasonable propagation window).
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Shareable URLs</h2>
          <p className="mt-2">
            Card pages have public, stable, shareable URLs. When you
            share a URL, the receiving site or app may fetch a preview
            image and description on your behalf; that preview may
            include your submission if it is the top-ranked photo on
            the card page at that moment.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Prices and market data</h2>
          <p className="mt-2">
            Market prices shown on card pages are attribution-only
            imports from public data sources and are not offers to buy
            or sell. CardCapture does not sell, grade, or authenticate
            cards.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Availability and warranty</h2>
          <p className="mt-2">
            CardCapture is provided &ldquo;as is&rdquo;, without warranty.
            We may change or discontinue features at any time. We make
            no promise about uptime or that any specific piece of content
            will remain available.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Contact</h2>
          <p className="mt-2">
            For questions, takedown requests, or account issues, email
            <a
              href="mailto:support@cardcapture.show"
              className="ml-1 text-indigo-text hover:underline"
              data-testid="terms-support-email"
            >
              support@cardcapture.show
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
